
import React from 'react';
import * as LucideIcons from 'lucide-react';
import { useModeAware } from '../hooks/useModeAware';
import { TabDefinition } from '../data/mode.constants';

interface AdaptiveTabNavProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  outputAvailable: boolean;
}

interface TabGroup {
  label: string;
  tabs: TabDefinition[];
}

function groupTabs(tabs: TabDefinition[]): TabGroup[] {
  const groups: TabGroup[] = [];
  const seen = new Set<string>();

  for (const tab of tabs) {
    if (!tab.group) continue;
    if (!seen.has(tab.group)) {
      seen.add(tab.group);
      groups.push({ label: tab.group, tabs: [] });
    }
    groups.find(g => g.label === tab.group)!.tabs.push(tab);
  }

  return groups;
}

export function AdaptiveTabNav({ activeTab, onTabChange, outputAvailable }: AdaptiveTabNavProps) {
  const { availableTabs, isQuickMode } = useModeAware();

  const mainTabs = availableTabs.filter(t => t.id !== 'output');
  const groups = groupTabs(mainTabs);
  const useGroupedLayout = !isQuickMode && groups.length > 1;

  // Build array of all visible tab IDs for arrow key navigation
  const allTabIds = [...mainTabs.map(t => t.id), ...(!isQuickMode ? ['compare'] : []), 'output'];

  const handleTabListKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = allTabIds.indexOf(activeTab);
    let nextIndex: number | null = null;

    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % allTabIds.length;
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + allTabIds.length) % allTabIds.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = allTabIds.length - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      const nextTabId = allTabIds[nextIndex];
      onTabChange(nextTabId);
      document.getElementById(`tab-${nextTabId}`)?.focus();
    }
  };

  return (
    <div
      className="flex items-end border-b border-slate-800 mb-8 overflow-x-auto scrollbar-hide"
      role="tablist"
      aria-label="Main navigation"
      onKeyDown={handleTabListKeyDown}
    >
      {useGroupedLayout ? (
        // Advanced mode: grouped layout with dividers and labels
        groups.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && <div className="w-px h-8 bg-slate-800 mx-1 self-center flex-shrink-0" />}
            <div className="flex flex-col flex-shrink-0">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] px-3 mb-0.5">
                {group.label}
              </span>
              <div className="flex">
                {group.tabs.map(tab => {
                  const IconComponent = LucideIcons[tab.icon as keyof typeof LucideIcons] as React.ComponentType<{ size: number }> | undefined;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      id={`tab-${tab.id}`}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`tabpanel-${tab.id}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => onTabChange(tab.id)}
                      className={`px-3 py-4 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 flex-shrink-0 transition-all border-b-2 ${
                        isActive
                          ? 'bg-slate-900 border-accent text-accent'
                          : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-900/50'
                      }`}
                    >
                      {IconComponent && <IconComponent size={13} />}
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        ))
      ) : (
        // Quick mode: simple flat rendering
        mainTabs.map(tab => {
          const IconComponent = LucideIcons[tab.icon as keyof typeof LucideIcons] as React.ComponentType<{ size: number }> | undefined;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onTabChange(tab.id)}
              className={`px-5 py-4 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 flex-shrink-0 transition-all border-b-2 ${
                isActive
                  ? 'bg-slate-900 border-accent text-accent'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-900/50'
              }`}
            >
              {IconComponent && <IconComponent size={13} />}
              {tab.label}
            </button>
          );
        })
      )}

      {/* Right side: Compare + Output (unchanged) */}
      <div className="ml-auto flex-shrink-0 flex items-end gap-2">
        {!isQuickMode && (
          <button
            id="tab-compare"
            role="tab"
            aria-selected={activeTab === 'compare'}
            aria-controls="tabpanel-compare"
            tabIndex={activeTab === 'compare' ? 0 : -1}
            onClick={() => onTabChange('compare')}
            className={`px-4 py-4 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 transition-all border-b-2 ${
              activeTab === 'compare'
                ? 'bg-slate-900 border-accent text-accent'
                : 'border-transparent text-slate-400 hover:text-accent'
            }`}
          >
            <LucideIcons.Scale size={13} />
            Compare
          </button>
        )}

        <button
          id="tab-output"
          role="tab"
          aria-selected={activeTab === 'output'}
          aria-controls="tabpanel-output"
          tabIndex={activeTab === 'output' ? 0 : -1}
          onClick={() => onTabChange('output')}
          className={`px-8 py-4 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-3 transition-all border-2 rounded-t-lg ${
            activeTab === 'output'
              ? 'bg-accent/20 border-accent text-accent shadow-[0_0_20px_rgba(245,158,11,0.2)]'
              : outputAvailable
                ? 'bg-slate-900 border-accent/40 text-accent animate-pulse shadow-[0_0_15px_rgba(192, 93, 62,0.1)] hover:border-accent'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
          }`}
        >
          {outputAvailable ? <LucideIcons.CheckCircle2 size={16} /> : <LucideIcons.FileText size={16} />}
          Output
        </button>
      </div>

      {isQuickMode && (
        <div className="absolute right-4 -top-8 animate-in fade-in slide-in-from-right-4 duration-500">
           <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">+10 more features in Advanced</span>
        </div>
      )}
    </div>
  );
}
